import { useEffect, type ReactElement } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth, useAuthenticatedClient } from "../../auth/AuthContext";
import { LoadingSpinner, ProgressBar } from "../../shared/components/Loading";
import { SPACING } from "../../shared/constants/spacing";
import { useAssetLoader, useOrientationLock } from "../../shared/hooks";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";
import { PlayerDemo } from "./components/PlayerDemo";

type LoadingStateProps = Readonly<{
  title: string;
  subtitle: string;
}>;

function LoadingState({ title, subtitle }: LoadingStateProps): ReactElement {
  return (
    <div className="h-screen bg-transparent flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md mx-auto px-8">
        <LoadingSpinner size="lg" />
        <div className={SPACING.player.controlSpacing}>
          <div className="text-white text-xl font-medium">{title}</div>
          <div className="text-neutral-400 text-sm">{subtitle}</div>
        </div>
        <ProgressBar indeterminate className="w-64 mx-auto" />
      </div>
    </div>
  );
}

type ErrorStateProps = Readonly<{
  error: string;
  onRetry: () => void;
}>;

function ErrorState({ error, onRetry }: ErrorStateProps): ReactElement {
  return (
    <div className="h-screen bg-transparent flex items-center justify-center">
      <div className="text-center">
        <div className="text-red-400 text-xl mb-4">{error}</div>
        <button onClick={onRetry} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded">
          Retry
        </button>
      </div>
    </div>
  );
}

export function PlayerPage(): ReactElement {
  const { id } = useParams(); // This could be slug or id
  const [searchParams] = useSearchParams();
  const assetIdParam = searchParams.get("assetId"); // Get assetId from query string
  const assetKindParam = searchParams.get("assetKind"); // Get assetKind for trailer playback
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const authenticatedClient = useAuthenticatedClient();

  // Lock to landscape orientation on all devices for optimal video viewing
  useOrientationLock("landscape");

  // Use transition navigation for smooth Player → Title transition
  const { transitionTo } = useTransitionNavigation();

  const { title, asset, loading, error, handleRetryLoad } = useAssetLoader({
    id,
    assetId: assetIdParam || undefined,
    assetKind: assetKindParam || undefined,
  });

  // Authentication guard - redirect to sign-in if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      // Create return URL to redirect back after sign-in
      const returnUrl = encodeURIComponent(globalThis.location.pathname + globalThis.location.search);
      navigate(`/sign-in?returnUrl=${returnUrl}`, { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleBack = (): void => {
    if (title?.slug) {
      // Use fullScreenFade for smooth Player → Title transition
      transitionTo(`/home/title/${title.slug}`, pageTransitions.fullScreenFade);
    } else {
      // Fallback to browser back if no title info
      navigate(-1);
    }
  };

  const handleVideoEnded = (): void => {
    // Only auto-navigate back to Title page if watching a trailer
    if (assetKindParam === "TRAILER" && title?.slug) {
      transitionTo(`/home/title/${title.slug}`, pageTransitions.cinematicFade);
    }
  };

  // Show authentication loading state
  if (authLoading) {
    return <LoadingState title="Verifying access" subtitle="Checking authentication..." />;
  }

  // Don't render if not authenticated (will redirect via useEffect)
  if (!user) {
    return <LoadingState title="Access required" subtitle="Redirecting to sign-in..." />;
  }

  if (loading) {
    return <LoadingState title="Preparing your video" subtitle="Optimizing stream quality and loading content..." />;
  }

  if (error || !title || !asset) {
    return <ErrorState error={error || "Video not found"} onRetry={handleRetryLoad} />;
  }

  return (
    <div className="h-screen bg-transparent relative">
      <PlayerDemo
        src={asset.hlsUrl}
        subtitles={asset.subtitles}
        onBack={handleBack}
        onEnded={handleVideoEnded}
        assetId={asset.id}
        titleId={title.id}
        user={user}
        authenticatedClient={authenticatedClient}
        vttUrl={asset.thumbnailVttUrl}
      />
    </div>
  );
}
